<script setup lang="ts">
/**
 * Primary side navigation.
 *
 * Layout strategy:
 * - The component lives in column 1 of App.vue's grid
 *   (`grid-cols-[64px_1fr] lg:grid-cols-[260px_1fr]`), so on `<lg` it
 *   naturally renders as a 64px icon strip and on `lg+` as a 260px panel.
 * - On `<lg` a hamburger in AppTopBar flips `mobileOpen`; we then promote
 *   the aside to a fixed 260px overlay drawer above the main column.
 *   App.vue renders a separate backdrop.
 */
import { onMounted, computed } from 'vue';
import { useRouter, RouterLink } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useBalanceStore } from '../stores/balance';
import { shortId, ngonkaToGnk } from '../lib/format';

defineProps<{ mobileOpen: boolean }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const auth = useAuthStore();
const balance = useBalanceStore();
const router = useRouter();

onMounted(() => {
  if (!balance.data && !balance.loading) void balance.load();
});

const navItems = [
  { to: '/dashboard', label: 'Overview', icon: 'overview' },
  { to: '/keys', label: 'API Keys', icon: 'keys' },
  { to: '/usage', label: 'Usage', icon: 'usage' },
  { to: '/billing', label: 'Billing', icon: 'billing' },
  { to: '/partner', label: 'Partner', icon: 'partner' },
  { to: '/docs', label: 'Docs', icon: 'docs' },
  { to: '/chat', label: 'Chat', icon: 'chat' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
] as const;

const balanceGnk = computed(() =>
  balance.data ? `${ngonkaToGnk(balance.data.balance_ngonka)} GNK` : '— GNK',
);

const userLabel = computed(() =>
  auth.me?.displayName ?? (auth.me ? shortId(auth.me.userId) : ''),
);

async function handleLogout(): Promise<void> {
  await auth.logout();
  await router.push({ name: 'login' });
}

function handleNavClick(): void {
  // Auto-close drawer on mobile after navigating.
  emit('close');
}
</script>

<template>
  <aside
    :class="[
      'bg-canvas border-r border-hairline flex flex-col h-full min-h-screen',
      mobileOpen
        ? 'fixed inset-y-0 left-0 w-64 z-50 lg:static lg:z-auto lg:w-auto'
        : 'relative',
    ]"
    role="navigation"
    aria-label="Primary"
  >
    <!-- Brand header -->
    <RouterLink
      to="/dashboard"
      class="flex items-center gap-2 px-4 py-4 border-b border-hairline h-14 text-ink focus-ring"
    >
      <span
        class="font-display font-bold tracking-tight whitespace-nowrap"
        :class="mobileOpen ? 'inline' : 'hidden lg:inline'"
      >Open Gonka</span>
      <!-- Collapsed rail (<lg): show the monogram as the mark. -->
      <span
        class="font-display font-bold text-lg"
        :class="mobileOpen ? 'hidden' : 'inline lg:hidden'"
      >OG</span>
    </RouterLink>

    <!-- Nav -->
    <ul class="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
      <li v-for="item in navItems" :key="item.to">
        <router-link
          :to="item.to"
          :aria-label="item.label"
          active-class="bg-surface-soft text-ink"
          class="flex items-center gap-3 px-3 py-2 rounded-full text-sm text-muted hover:text-ink hover:bg-surface-soft transition focus-ring"
          @click="handleNavClick"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="shrink-0"
            aria-hidden="true"
          >
            <!-- Overview: square stack -->
            <g v-if="item.icon === 'overview'">
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" />
            </g>
            <!-- API Keys: key -->
            <g v-else-if="item.icon === 'keys'">
              <circle cx="8" cy="15" r="4" />
              <path d="M10.85 12.15L21 2" />
              <path d="M18 5l3 3" />
              <path d="M15 8l2 2" />
            </g>
            <!-- Usage: bar chart -->
            <g v-else-if="item.icon === 'usage'">
              <line x1="4" y1="20" x2="20" y2="20" />
              <rect x="6" y="11" width="3" height="9" />
              <rect x="11" y="6" width="3" height="14" />
              <rect x="16" y="14" width="3" height="6" />
            </g>
            <!-- Billing: credit card -->
            <g v-else-if="item.icon === 'billing'">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
              <line x1="6" y1="15" x2="10" y2="15" />
            </g>
            <!-- Partner: users -->
            <g v-else-if="item.icon === 'partner'">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </g>
            <!-- Docs: book open -->
            <g v-else-if="item.icon === 'docs'">
              <path d="M2 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H2z" />
              <path d="M22 4h-7a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h8z" />
            </g>
            <!-- Chat: message square -->
            <g v-else-if="item.icon === 'chat'">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </g>
            <!-- Settings: gear -->
            <g v-else-if="item.icon === 'settings'">
              <circle cx="12" cy="12" r="3" />
              <path
                d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.07.16.11.34.11.51 0 .55.45 1 1 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
              />
            </g>
          </svg>
          <span
            class="whitespace-nowrap"
            :class="mobileOpen ? 'inline' : 'hidden lg:inline'"
          >{{ item.label }}</span>
        </router-link>
      </li>
    </ul>

    <!-- Footer: user + balance -->
    <div
      v-if="auth.me"
      class="mt-auto border-t border-hairline px-3 py-3 flex items-center gap-2"
    >
      <div
        class="flex-1 min-w-0"
        :class="mobileOpen ? 'block' : 'hidden lg:block'"
      >
        <div class="text-sm text-ink truncate">{{ userLabel }}</div>
        <div class="text-xs text-muted truncate">{{ balanceGnk }}</div>
      </div>
      <button
        type="button"
        class="p-2 rounded-full text-muted hover:text-danger hover:bg-surface-soft transition focus-ring"
        aria-label="Log out"
        @click="handleLogout"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      </button>
    </div>
  </aside>
</template>
